import React, { useState } from "react";
import { NavLink, Routes, Route, useNavigate } from "react-router-dom";
import { logout } from "../../utils/auth";
import {
  LayoutDashboard,
  Fish,
  Droplets,
  Users,
  Package,
  Truck,
  Shield,
  Bell,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Camera,
} from "lucide-react";

import Feeding from "./feeding";
import Aerators from "./aerator";
import Staff from "./staff";
import FeedStorage from "./feedstorage";
import Vehicles from "./vehicle";
import Security from "./security";
import Alerts from "./alerts";
import CameraSetup from "./camera";

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/growelhome", exact: true },
  { label: "Camera Setup", icon: Camera, path: "/growelhome/camera-setup" },
  { label: "Feeding", icon: Fish, path: "/growelhome/feeding" },
  { label: "Aerators", icon: Droplets, path: "/growelhome/aerators" },
  { label: "Staff", icon: Users, path: "/growelhome/staff" },
  { label: "Storage", icon: Package, path: "/growelhome/feed-storage" },
  { label: "Vehicles", icon: Truck, path: "/growelhome/vehicles" },
  { label: "Security", icon: Shield, path: "/growelhome/security" },
  { label: "Alerts", icon: Bell, path: "/growelhome/alerts" },
];

const kpiData = [
  { label: "Feeding Sessions", value: "4/4", status: "optimal", text: "On schedule" },
  { label: "Avg Duration", value: "25m", status: "optimal", text: "Optimal" },
  { label: "Aerators", value: "7/8", status: "warning", text: "1 stopped" },
  { label: "Night Compliance", value: "100%", status: "optimal", text: "OK" },
  { label: "Aerator Stops", value: "0", status: "optimal", text: "None" },
  { label: "Staff On-Site", value: "3", status: "warning", text: "1 idle" },
  { label: "Arrival Time", value: "100%", status: "optimal", text: "Compliant" },
  { label: "Feed Removed", value: "12", status: "optimal", text: "Authorised" },
  { label: "Unauthorized Entry", value: "0", status: "optimal", text: "Secure" },
  { label: "Vehicles", value: "5", status: "critical", text: "1 unauth" },
  { label: "Perimeter", value: "0", status: "optimal", text: "Secure" },
  { label: "Alerts", value: "2", status: "critical", text: "Action needed" },
  { label: "Night Movement", value: "1", status: "warning", text: "Flagged" },
  { label: "Restricted Zone", value: "0", status: "optimal", text: "None" },
];

const moduleCards = [
  {
    title: "Feeding Monitoring",
    status: "optimal",
    metrics: [
      { label: "Sessions", value: "4/4" },
      { label: "Duration", value: "25m avg" },
      { label: "Worker Present", value: "100%" },
    ],
    detections: ["Human presence", "Feed motion", "Timestamp validation"],
  },
  {
    title: "Aerator Monitoring",
    status: "warning",
    statusText: "1 Stopped",
    metrics: [
      { label: "Running", value: "7/8" },
      { label: "Night Mode", value: "100%" },
      { label: "Sudden Stops", value: "0" },
    ],
    detections: ["Paddle rotation", "Water movement"],
  },
  {
    title: "Staff Presence",
    status: "warning",
    statusText: "1 Idle",
    metrics: [
      { label: "On-Site", value: "3" },
      { label: "Arrival", value: "Compliant" },
      { label: "Idle Zones", value: "1" },
    ],
    detections: ["Person detection", "Zone movement"],
  },
  {
    title: "Feed Storage",
    status: "optimal",
    metrics: [
      { label: "Bags Removed", value: "12" },
      { label: "Unauthorized", value: "0" },
      { label: "Room Open", value: "24m" },
    ],
    detections: ["Object movement", "Person detection"],
  },
  {
    title: "Vehicle Monitoring",
    status: "critical",
    statusText: "1 Unauth",
    metrics: [
      { label: "Logged", value: "5" },
      { label: "Arrivals", value: "3" },
      { label: "Unauthorized", value: "1" },
    ],
    detections: ["Entry/Exit", "Time logging"],
  },
  {
    title: "Security Monitoring",
    status: "critical",
    statusText: "2 Alerts",
    metrics: [
      { label: "Night Move", value: "1" },
      { label: "Breach", value: "0" },
      { label: "Intrusions", value: "0" },
    ],
    detections: ["Motion detect", "Zone intrusion", "Person in zone"],
  },
];

const getStatusColor = (status) => {
  switch (status) {
    case "optimal":
      return { bg: "bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", indicator: "text-emerald-600" };
    case "warning":
      return { bg: "bg-amber-50", badge: "bg-amber-100 text-amber-700", dot: "bg-amber-500", indicator: "text-amber-600" };
    case "critical":
      return { bg: "bg-red-50", badge: "bg-red-100 text-red-700", dot: "bg-red-500", indicator: "text-red-600" };
    default:
      return { bg: "bg-gray-50", badge: "bg-gray-100 text-gray-700", dot: "bg-gray-500", indicator: "text-gray-600" };
  }
};

const DashboardHome = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Farm Dashboard</h1>
      <p className="text-gray-500">Real-time AI vision analytics</p>
    </div>

    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Key Performance Indicators</h2>
        <span className="text-xs text-emerald-600 font-medium">Live</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {kpiData.map((kpi, i) => {
          const colors = getStatusColor(kpi.status);
          return (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-all group"
            >
              <div className="text-xs text-gray-500 font-medium mb-3">{kpi.label}</div>
              <div className="text-2xl font-bold text-gray-900 mb-3">{kpi.value}</div>
              <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg ${colors.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                {kpi.text}
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">AI Detection Modules</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {moduleCards.map((module, idx) => {
          const colors = getStatusColor(module.status);
          return (
            <div
              key={idx}
              className={`${colors.bg} border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-all`}
            >
              <div className="flex items-start justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">{module.title}</h3>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg ${colors.badge}`}>
                  {module.statusText || "Active"}
                </span>
              </div>

              <div className="space-y-3 mb-5 pb-5 border-b border-gray-300">
                {module.metrics.map((m, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{m.label}</span>
                    <span className="font-semibold text-gray-900">{m.value}</span>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Detections</div>
                <ul className="space-y-1.5">
                  {module.detections.map((det, i) => (
                    <li key={i} className="text-xs text-gray-700 flex items-start gap-2">
                      <span className={`${colors.indicator} mt-1`}>●</span>
                      {det}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const GrowelHome = () => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 h-16 flex items-center px-6 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br flex items-center justify-center text-white font-bold text-sm">
            <img
                src="/logo3.png" // replace with your logo path
                alt="CogniVision Logo"
                className="w-8 h-8 object-contain"
              />
          </div>
          <span className="font-semibold text-gray-900 text-lg">CogniVision</span>
        </div>

        <button
          onClick={handleLogout}
          className="ml-auto flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm transition"
        >
          <LogOut size={16} />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside
          className={`fixed top-16 left-0 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 transition-all duration-300 z-30 flex flex-col ${
            isCollapsed ? "w-20" : "w-64"
          }`}
        >
          <div className="flex justify-end p-3">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 rounded-lg hover:bg-gray-100 transition text-gray-600"
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>

          <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-gray-600 hover:bg-gray-50 border border-transparent"
                  } ${isCollapsed ? "justify-center" : ""}`
                }
              >
                <item.icon size={18} />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <div className={`text-xs text-gray-500 ${isCollapsed ? "text-center" : ""}`}>
              {!isCollapsed && "Last updated moments ago"}
            </div>
          </div>
        </aside>

        <main
          className={`flex-1 overflow-y-auto transition-all duration-300 ${
            isCollapsed ? "ml-20" : "ml-64"
          }`}
        >
          <div className="p-6 max-w-7xl mx-auto">
            <Routes>
              <Route index element={<DashboardHome />} />
              <Route path="camera-setup" element={<CameraSetup />} />
              <Route path="feeding" element={<Feeding />} />
              <Route path="aerators" element={<Aerators />} />
              <Route path="staff" element={<Staff />} />
              <Route path="feed-storage" element={<FeedStorage />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="security" element={<Security />} />
              <Route path="alerts" element={<Alerts />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

export default GrowelHome;